'use client';

import { useMemo } from 'react';

import { SidebarMenuBadge, SidebarNavigation } from '@kit/ui/shadcn-sidebar';

import {
  useNavigationBadgeCounts,
  type NavigationBadgeCounts,
} from '../_lib/hooks/use-navigation-badges';
import type { UserWorkspace } from '../_lib/server/load-user-workspace';

type NavigationConfig = React.ComponentProps<
  typeof SidebarNavigation
>['config'];

/**
 * Maps route paths to badge count keys
 */
const BADGE_PATH_MAPPING: Record<string, keyof NavigationBadgeCounts> = {
  '/home/inbox': 'chatUnread',
  '/home/assignments': 'pendingAssignments',
  '/home/feedback': 'pendingFeedback',
  '/home/reimbursements': 'reimbursementsNeedingAction',
  '/admin/hire-orders': 'adminPendingHireOrders',
  // Canonical admin menu path is /admin/finance/artist-invoices (was the stale
  // /admin/finance/dancer-invoices, which never matched so the badge dropped).
  '/admin/finance/artist-invoices': 'adminPendingInvoices',
  '/admin/finance/reimbursements': 'adminPendingReimbursements',
  '/home/client-user/invoices': 'clientPendingInvoices',
};

/**
 * Creates a badge element if count > 0
 */
function createBadge(count: number) {
  if (count <= 0) return undefined;
  return <SidebarMenuBadge>{count}</SidebarMenuBadge>;
}

/**
 * Injects badge renderAction into navigation config routes based on badge counts
 */
function injectBadgesIntoConfig(
  config: NavigationConfig,
  counts: NavigationBadgeCounts,
): NavigationConfig {
  return {
    ...config,
    routes: config.routes.map((route) => {
      if ('divider' in route) return route;

      return {
        ...route,
        children: route.children.map((child) => {
          if (!('path' in child)) return child;

          const badgeKey = BADGE_PATH_MAPPING[child.path];
          if (!badgeKey) return child;

          const count = counts[badgeKey];
          const badge = createBadge(count);
          if (!badge) return child;

          return {
            ...child,
            renderAction: badge,
          };
        }),
      };
    }),
  };
}

export function HomeSidebarNavigationWithBadges(props: {
  config: NavigationConfig;
  workspace: UserWorkspace;
}) {
  const { user, dancerProfileId, isSuperAdmin, clientId } = props.workspace;
  const { counts } = useNavigationBadgeCounts({
    userId: user.id,
    dancerProfileId,
    isSuperAdmin,
    clientId,
  });

  const configWithBadges = useMemo(
    () => injectBadgesIntoConfig(props.config, counts),
    [props.config, counts],
  );

  return <SidebarNavigation config={configWithBadges} />;
}
